import React, { useCallback, useEffect } from 'react'
import * as rawStyles from './styles.module.scss'
import { Controller } from 'react-hook-form'
import Input from '../../basics/input'
import ClickableArea from '../../basics/clickable-area/component'
import Select from '../../basics/select'
import Icon from '../../basics/icon'
import { InputCrossIcon } from '../../basics/icons'
import { DynamicFormProps } from './types'
import Textarea from '../../basics/textarea/component'
import Checkbox from '../../basics/checkbox/component'
import ReCAPTCHA from 'react-google-recaptcha'
import axios from 'axios'
import { CLIENT_SITE_KEY, RE_CAPTCHA_API, RE_CAPTCHA_POST_URL, SCRIPT_SITE_KEY } from './constants'

const DynamicForm: React.FC<DynamicFormProps> = ({
    status = 'empty',
    fields,
    form,
    action,
    submitButtonLabel,
    responseMessage,
    dataLayer,
    children,
}) => {
    const styles: any = rawStyles
    const {
        formState: { errors, isValid },
        trigger,
        watch,
    } = form

    const [previouslyPassedRecaptcha, setPreviouslyPassedRecaptcha] = React.useState(false)
    const [passedRecaptcha, setPassedRecaptcha] = React.useState(false)

    const isTermsOfUseChecked = watch('Terms Of Use') === 'on'

    const isSubmitButtonDisabled = !(
        isValid &&
        isTermsOfUseChecked &&
        (passedRecaptcha || previouslyPassedRecaptcha)
    )

    const handleScriptLoaded = useCallback(() => {
        window.grecaptcha.ready(() => {
            window.grecaptcha
                .execute(SCRIPT_SITE_KEY, {
                    action: 'verify',
                })
                .then((token) => {
                    axios
                        .post(`${RE_CAPTCHA_POST_URL}?token=${token}`)
                        .then((res) => {
                            const data = res.data
                            const score = data.response.score
                            if (score > 0.8) setPreviouslyPassedRecaptcha(true)
                        })
                        .catch((error) => {
                            console.error('Axios error:', error)
                        })
                })
                .catch((error) => {
                    console.error('reCAPTCHA error:', error)
                })
        })
    }, [])

    useEffect(() => {
        const script = document.createElement('script')
        script.src = RE_CAPTCHA_API
        script.addEventListener('load', handleScriptLoaded)
        document.body.appendChild(script)

        return () => {
            script.removeEventListener('load', handleScriptLoaded)
            document.body.removeChild(script)
        }
    }, [handleScriptLoaded])

    useEffect(() => {
        if (
            typeof window !== 'undefined' &&
            window.dataLayer &&
            status === 'success' &&
            dataLayer
        ) {
            window.dataLayer.push({ ...dataLayer })
        }
    }, [status, dataLayer])

    return (
        <div className={styles.c_dynamic_form}>
            {status === 'success' || status === 'error' ? (
                <div className={styles.wrapper}>
                    <h5 className={styles.title}>{responseMessage.title}</h5>
                    <p className={styles.description}>{responseMessage.description}</p>
                    {responseMessage.buttonLink && (
                        <ClickableArea
                            href={responseMessage.buttonLink}
                            variant='primary'
                            color='orange'>
                            {responseMessage.buttonLabel}
                        </ClickableArea>
                    )}
                </div>
            ) : (
                <form
                    action={isSubmitButtonDisabled ? undefined : action}
                    acceptCharset='UTF-8'
                    method='POST'>
                    <div className={styles.form}>
                        <div className={styles.fields}>
                            {fields.map((field) => (
                                <Controller
                                    key={field.name}
                                    control={form.control}
                                    name={field.name}
                                    rules={field.validation}
                                    render={({ field: controllerField }) =>
                                        field.type === 'input' ? (
                                            <Input
                                                className={styles.input}
                                                name={field.name}
                                                type='text'
                                                label={field.label}
                                                value={controllerField.value}
                                                error={!!errors[controllerField.name]}
                                                errorMessage={`Please introduce a valid ${field.label.toLowerCase()}`}
                                                errorIcon={
                                                    <Icon
                                                        className={styles.error_icon}
                                                        icon={<InputCrossIcon />}
                                                    />
                                                }
                                                onChange={controllerField.onChange}
                                                onBlur={() => trigger(controllerField.name)}
                                            />
                                        ) : field.type === 'select' ? (
                                            <Select
                                                button={
                                                    <Select.Trigger
                                                        className={styles.trigger}
                                                        placeholder={field.label}
                                                    />
                                                }
                                                name={field.name}
                                                value={controllerField.value}
                                                onValueChange={controllerField.onChange}>
                                                <Select.Group>
                                                    <Select.Label>{field.label}</Select.Label>
                                                    {field.options?.map((option) => (
                                                        <Select.Item
                                                            key={option.value}
                                                            className={styles.item}
                                                            value={option.value}
                                                            selectedIcon={
                                                                <span
                                                                    className={styles.icon}></span>
                                                            }>
                                                            {option.label}
                                                        </Select.Item>
                                                    ))}
                                                </Select.Group>
                                            </Select>
                                        ) : (
                                            <Textarea
                                                id={field.name}
                                                required
                                                name={field.name}
                                                placeholder={field.label}
                                                defaultValue={field.defaultValue}
                                                error={!!errors[controllerField.name]}
                                                errorMessage={`Please introduce a valid ${field.label.toLowerCase()}`}
                                                onChange={controllerField.onChange}
                                                onBlur={controllerField.onBlur}
                                            />
                                        )
                                    }
                                />
                            ))}

                            <Controller
                                control={form.control}
                                name='Terms Of Use'
                                rules={{ required: 'You must agree to the terms of use' }}
                                render={({ field: controllerField }) => (
                                    <div>
                                        <Checkbox
                                            name='Terms Of Use'
                                            checked={controllerField.value === 'on'}
                                            label={
                                                <div className={styles.checkbox_label}>
                                                    By submitting the form, you agree to our{' '}
                                                    <a href='/legals/terms-and-conditions'>
                                                        Terms of Use
                                                    </a>{' '}
                                                    and <a href='/legals/policy'>Privacy Policy</a>
                                                </div>
                                            }
                                            onChange={(e) => {
                                                controllerField.onChange(
                                                    e.target.checked ? 'on' : '',
                                                )
                                            }}
                                        />
                                    </div>
                                )}
                            />
                        </div>

                        {children}

                        {!previouslyPassedRecaptcha && (
                            <Controller
                                control={form.control}
                                name='g-recaptcha-response'
                                render={() => (
                                    <ReCAPTCHA
                                        sitekey={CLIENT_SITE_KEY}
                                        onChange={(token: string) => setPassedRecaptcha(!!token)}
                                    />
                                )}
                            />
                        )}

                        <div className={styles.ctas_container}>
                            <button
                                disabled={isSubmitButtonDisabled}
                                className={styles.button}
                                type='submit'>
                                <ClickableArea
                                    variant='primary'
                                    color='orange'
                                    disabled={isSubmitButtonDisabled}>
                                    {submitButtonLabel}
                                </ClickableArea>
                            </button>
                        </div>
                    </div>
                </form>
            )}
        </div>
    )
}

export default DynamicForm
